import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Spinner,
} from "reactstrap";
// Reuse the same CSS you already built for the single-receipt viewer.
// It already defines .refund-container / .refund-overlay / .r-* classes
// with the correct positions over PaymentRefund.png.
import api from "../../services/api";
import "./RefundInvoice.css";

const RefundReceiptByRollNo = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const [rollNo, setRollNo] = useState("");
  const [refundsData, setRefundsData] = useState([]);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [user, setUser] = useState();
  const [generatingId, setGeneratingId] = useState(null);

  useEffect(() => {
    const authUser = JSON.parse(localStorage.getItem("authUser"));
    setUser(authUser);
  }, []);

  // Fetch student details + refunds
  const handleFetchRefundsData = async () => {
    if (!rollNo.trim()) {
      setErrorMsg("Please enter Roll No.");
      return;
    }

    setErrorMsg("");
    setLoading(true);
    setStudentData(null);
    setRefundsData([]);

    try {
      const res = await api.post(
        `${backendUrl}get-student-refunds-by-rollno`,
        {
          roll_no: rollNo.toUpperCase().trim(),
        }
      );

      if (res.data.success && res.data.data.length > 0) {
        setRefundsData(res.data.data);

        // Extract student details from first record
        const st = res.data.data[0];
        console.log("Student details:", st);
        setStudentData({
          name: st.name,
          roll_no: st.roll_no,
          college: st.college,
          branch: st.branch,
          passout_year: st.passout_year,
        });
      } else {
        setErrorMsg(res.data.message || "No student/refund records found.");
      }
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Something went wrong.");
    }

    setLoading(false);
  };

  // Format IST Date (date only, matches RefundReceipt.js formatDate)
  const formatISTDate = (isoDate) => {
    if (!isoDate) return "N/A";
    return new Date(isoDate)
      .toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
      .replaceAll("/", "-");
  };

  // Shrink cashier/given-by name to fit a single line — same as RefundReceipt.js
  const fitSingleLine = (invoiceDiv, selector) => {
    const el = invoiceDiv.querySelector(selector);
    if (!el) return;

    el.style.transform = "none";
    el.style.whiteSpace = "nowrap";

    const clone = el.cloneNode(true);
    clone.style.position = "fixed";
    clone.style.visibility = "hidden";
    clone.style.left = "0";
    clone.style.top = "0";
    clone.style.width = "auto";
    clone.style.whiteSpace = "nowrap";
    document.body.appendChild(clone);

    const textWidth = clone.scrollWidth;
    document.body.removeChild(clone);

    const maxWidth = invoiceDiv.offsetWidth * 0.22;
    if (textWidth > maxWidth && maxWidth > 0) {
      const scale = maxWidth / textWidth;
      el.style.transform = `scaleX(${scale})`;
      el.style.transformOrigin = "left center";
    }
  };

  // Generate Refund Receipt PDF for a single row (off-screen render, same
  // pattern as generateInvoicePDF in PaymentReceipt.js, but using the
  // .refund-container / .r-* markup from RefundInvoice.css)
  const generateRefundPDF = async (r) => {
    setGeneratingId(r.payment_id);

    const cashierName = r.refund_given_by || user?.first_name || "";
    const formattedPaymentDate = formatISTDate(r.payment_date);
    const formattedGivenDate = formatISTDate(r.refund_given_date);

    const invoiceDiv = document.createElement("div");
    invoiceDiv.className = "refund-container";
    invoiceDiv.style.position = "absolute";
    invoiceDiv.style.left = "-9999px";

    invoiceDiv.innerHTML = `
      <div class="refund-overlay">

        <!-- ORIGINAL COPY -->
        <h1 class="r-payment-id">No.${r.payment_id}</h1>
        <h1 class="r-payment-date">${formattedPaymentDate}</h1>
        <h2 class="r-name">${r.name || "N/A"}</h2>
        <h2 class="r-roll-no">${r.roll_no || "N/A"}</h2>
        <h2 class="r-branch">${r.branch || "N/A"}</h2>
        <h2 class="r-college">${r.college || "N/A"}</h2>
        <h2 class="r-passout">${r.passout_year || "N/A"}</h2>
        <h2 class="r-course">${r.course_name || "N/A"}</h2>
        <h2 class="r-technology">${r.technology_name || "N/A"}</h2>
        <h2 class="r-amount">${r.refund_amount || "N/A"}</h2>
        <h2 class="r-term">${r.term || "N/A"}</h2>
        <h2 class="r-cashier">${cashierName}</h2>

        <!-- DUPLICATE COPY -->
        <h1 class="r-payment-id-1">No.${r.payment_id}</h1>
        <h1 class="r-payment-date-1">${formattedPaymentDate}</h1>
        <h2 class="r-name-1">${r.student_name || "N/A"}</h2>
        <h2 class="r-roll-no-1">${r.roll_no || "N/A"}</h2>
        <h2 class="r-branch-1">${r.branch || "N/A"}</h2>
        <h2 class="r-college-1">${r.college || "N/A"}</h2>
        <h2 class="r-passout-1">${r.passout_year || "N/A"}</h2>
        <h2 class="r-course-1">${r.course_name || "N/A"}</h2>
        <h2 class="r-technology-1">${r.technology_name || "N/A"}</h2>
        <h2 class="r-amount-1">${r.refund_amount || "N/A"}</h2>
        <h2 class="r-term-1">${r.term || "N/A"}</h2>
        <h2 class="r-cashier-1">${cashierName}</h2>

      </div>
    `;

    document.body.appendChild(invoiceDiv);

    // Shrink cashier/given-by name to single line, same as RefundReceipt.js
    fitSingleLine(invoiceDiv, ".r-cashier");
    fitSingleLine(invoiceDiv, ".r-cashier-1");

    try {
      const canvas = await html2canvas(invoiceDiv, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const width = pdf.internal.pageSize.getWidth();
      const height = (canvas.height * width) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, width, height);

      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Refund PDF generation error:", err);
    } finally {
      document.body.removeChild(invoiceDiv);
      setGeneratingId(null);
    }
  };

  return (
    <div className="mt-4">
      {/* Search Card */}
      <Card className="shadow-sm border-0 mb-4" style={{ borderRadius: "12px" }}>
        <CardBody>
          <Row className="align-items-end g-3">
            <Col md={2}>
              <label className="fw-semibold">Roll Number</label>
              <Input
                type="text"
                value={rollNo}
                onChange={(e) => setRollNo(e.target.value)}
                placeholder="Enter Student Roll No"
                bsSize="md"
              />
            </Col>

            <Col md={2}>
              <Button
                color="primary"
                size="md"
                className="w-100 fw-semibold"
                disabled={loading}
                onClick={handleFetchRefundsData}
              >
                {loading ? <Spinner size="sm" /> : "Get Refunds"}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {errorMsg && <Alert color="danger">{errorMsg}</Alert>}

      {/* Student Details Card */}
      {studentData && (
        <Card className="shadow-sm border-0 mb-4 w-75" style={{ borderRadius: "12px" }}>
          <CardHeader
            className="bg-primary"
            style={{ color: "white", fontWeight: "bold", fontSize: "18px" }}
          >
            Student Details
          </CardHeader>

          <CardBody style={{ padding: "25px" }}>
            <Row className="gy-3 fs-5">
              <Col md={3}>
                <div className="detail-label fw-bold">Name</div>
                <div className="detail-value">{studentData.name}</div>
              </Col>

              <Col md={2}>
                <div className="detail-label fw-bold">Roll Number</div>
                <div className="detail-value">{studentData.roll_no}</div>
              </Col>

              <Col md={2}>
                <div className="detail-label fw-bold">College</div>
                <div className="detail-value">{studentData.college}</div>
              </Col>

              <Col md={2}>
                <div className="detail-label fw-bold">Branch</div>
                <div className="detail-value">{studentData.branch}</div>
              </Col>

              <Col md={2}>
                <div className="detail-label fw-bold">Passout Year</div>
                <div className="detail-value">{studentData.passout_year}</div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      )}

      {/* Refunds Table */}
      {refundsData.length > 0 && (
        <Card className="shadow-sm border-0" style={{ borderRadius: "12px" }}>
          <CardHeader
            className="bg-primary"
            style={{ color: "white", fontWeight: "bold", fontSize: "18px" }}
          >
            Refund Receipts
          </CardHeader>

          <CardBody className="p-0">
            <table className="table table-hover mb-0 payment-table">
              <thead>
                <tr>
                  <th>Course</th>
                  <th>Technology</th>
                  <th>Payment ID</th>
                  <th>Term</th>
                  <th>Refund Amount</th>
                  <th>Reason</th>
                  <th>Cash Counter</th>
                  <th>Given By</th>
                  <th>Given Date</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {refundsData.map((ele, idx) => (
                  <tr key={idx}>
                    <td>{ele.course_name}</td>
                    <td>{ele.technology_name}</td>
                    <td>
                      <b>{ele.payment_id}</b>
                    </td>
                    <td>{ele.term}</td>
                    <td>
                      <b style={{ color: "#c0392b" }}>₹{ele.refund_amount}</b>
                    </td>
                    <td>{ele.refund_reason}</td>
                    <td>{ele.refund_cashcounter}</td>
                    <td>{ele.refund_given_by}</td>
                    <td>{formatISTDate(ele.refund_given_date)}</td>

                    <td className="text-center">
                      <Button
                        color="success"
                        size="sm"
                        style={{ fontWeight: "600" }}
                        disabled={generatingId === ele.payment_id}
                        onClick={() => generateRefundPDF(ele)}
                      >
                        {generatingId === ele.payment_id ? (
                          <Spinner size="sm" />
                        ) : (
                          "View"
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default RefundReceiptByRollNo;